import React, { useState, useRef, useEffect } from 'react';
import styles from './DatePicker.module.scss';
import classNames from 'classnames';
import collapseIcon from '../../../assets/collapse-black.svg';
import Input from '../Input/Input';
import { formatDateString, DATE_FORMATS } from '../../../utils/dateUtils';

interface DatePickerProps {
    value?: string;
    onChange?: (date: string) => void;
    min?: Date | string;
    max?: Date | string;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    unit?: React.ReactNode;
    displayFormat?: string;
    responseFormat?: string;
    useFixedPosition?: boolean;
    calendarWidth?: number;
    hideInput?: boolean;
    isOpen?: boolean;
    showFloatingLabel?: boolean;
    unitStyle?: React.CSSProperties;
}

export const DatePicker: React.FC<DatePickerProps> = ({
    value,
    onChange,
    min,
    max,
    disabled = false,
    className,
    placeholder = 'Select date',
    unit,
    displayFormat = DATE_FORMATS.DATE_FORMAT_DDMMYYYY,
    responseFormat = DATE_FORMATS.ISO,
    useFixedPosition = false,
    calendarWidth = 264,
    showFloatingLabel = false,
    unitStyle,
    hideInput = false,
    isOpen: isOpenProp = false,
}) => {
    const [currentDate, setCurrentDate] = useState(value ? new Date(value) : new Date());
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value ? new Date(value) : undefined);
    const calendarRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);
    const [initialPositionCalculated, setInitialPositionCalculated] = useState(false);

    // Internal state for managing calendar open/close when isOpen prop is not provided
    const [isOpenInternal, setIsOpenInternal] = useState(false);

    // Use isOpenProp if provided, otherwise use internal state
    const isOpen = isOpenProp !== false ? isOpenProp : isOpenInternal;

    // Calculate initial position when calendar is about to open
    const calculateInitialPosition = () => {
        if (useFixedPosition) {
            const referenceElement = inputRef.current || containerRef.current;
            if (!referenceElement) return;

            const inputRect = referenceElement.getBoundingClientRect();
            const estimatedCalendarHeight = 400;
            const calendarW = calendarWidth;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = inputRect.bottom + 5;
            if (top + estimatedCalendarHeight > viewportHeight) {
                top = Math.max(10, viewportHeight - estimatedCalendarHeight - 10);
            }

            let left = inputRect.right - calendarW;
            if (left < 10) {
                left = 10;
            } else if (left + calendarW > viewportWidth) {
                left = Math.max(10, viewportWidth - calendarW - 10);
            }

            setCalendarPosition({ top, left });
            setInitialPositionCalculated(true);
        }
    };

    const updatePosition = () => {
        if (useFixedPosition && calendarRef.current) {
            const referenceElement = inputRef.current || containerRef.current;
            if (!referenceElement) return;

            const inputRect = referenceElement.getBoundingClientRect();
            const estimatedCalendarHeight = 400;
            const calendarW = calendarWidth;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            let top = inputRect.bottom + 5;
            if (top + estimatedCalendarHeight > viewportHeight) {
                top = Math.max(10, viewportHeight - estimatedCalendarHeight - 10);
            }

            let left = inputRect.right - calendarW;
            if (left < 10) {
                left = 10;
            } else if (left + calendarW > viewportWidth) {
                left = Math.max(10, viewportWidth - calendarW - 10);
            }

            setCalendarPosition({ top, left });
            setIsPositioned(true);
        }
    };

    // Reset positioning when opening
    useEffect(() => {
        if (isOpen) {
            setIsPositioned(false);
            setInitialPositionCalculated(false);
            // Calculate position immediately when opening
            if (useFixedPosition) {
                calculateInitialPosition();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && useFixedPosition) {
            // Remove the setTimeout and calculate position immediately
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition);

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition);
            };
        } else if (!isOpen) {
            setIsPositioned(false);
        }
    }, [isOpen, useFixedPosition]);

    // Update position after calendar is rendered
    useEffect(() => {
        if (isOpen && useFixedPosition && initialPositionCalculated) {
            // Use requestAnimationFrame to ensure the calendar is rendered
            requestAnimationFrame(() => {
                updatePosition();
            });
        }
    }, [isOpen, useFixedPosition, initialPositionCalculated]);

    useEffect(() => {
        if (isOpen && useFixedPosition) {
            updatePosition();
        }
    }, [currentDate, isOpen, useFixedPosition]);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // If hideInput is true, let the parent component handle click-outside logic
            if (hideInput) {
                return;
            }

            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                ((inputRef.current && !inputRef.current.contains(event.target as Node)) ||
                    (hideInput && containerRef.current && !containerRef.current.contains(event.target as Node)))
            ) {
                // Close internal state if we're using it
                if (isOpenProp === false) {
                    setIsOpenInternal(false);
                }
                // Let parent component handle closing if using isOpen prop
                // The parent should detect this and set isOpen to false
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, hideInput, isOpenProp]);

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const isDateDisabled = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let minDate: Date | null = null;
        let maxDate: Date | null = null;

        // Handle min date
        if (min) {
            if (min === '0') {
                minDate = today;
            } else if (!isNaN(Number(min))) {
                // Relative days from today
                minDate = new Date(today);
                minDate.setDate(today.getDate() + Number(min.toString()));
            } else if (!isNaN(Date.parse(min.toString()))) {
                // Absolute date
                minDate = new Date(min.toString());
            }
            if (minDate) minDate.setHours(0, 0, 0, 0);
        }

        // Handle max date
        if (max) {
            if (max === '0') {
                maxDate = today;
            } else if (!isNaN(Number(max))) {
                // Relative days from today
                maxDate = new Date(today);
                maxDate.setDate(today.getDate() + Number(max.toString()));
            } else if (!isNaN(Date.parse(max.toString()))) {
                // Absolute date
                maxDate = new Date(max.toString());
            }
            if (maxDate) maxDate.setHours(23, 59, 59, 999);
        }

        // Apply the constraints
        if (minDate && date < minDate) return true;
        if (maxDate && date > maxDate) return true;

        return false;
    };

    const handleDateSelect = (day: number) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        if (isDateDisabled(newDate)) return;

        setSelectedDate(newDate);
        onChange?.(formatDateString(newDate, responseFormat));
        setIsPositioned(false);

        // Close internal state if we're using it
        if (isOpenProp === false) {
            setIsOpenInternal(false);
        }
    };

    const handleOtherMonthDateSelect = (day: number, monthOffset: number) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + monthOffset, day);
        if (isDateDisabled(newDate)) return;

        changeMonth(monthOffset);
        setSelectedDate(newDate);
        onChange?.(formatDateString(newDate, responseFormat));
        setIsPositioned(false);

        // Close internal state if we're using it
        if (isOpenProp === false) {
            setIsOpenInternal(false);
        }
    };

    const changeMonth = (offset: number) => {
        setCurrentDate((prev) => {
            const newDate = new Date(prev.getFullYear(), prev.getMonth() + offset, 1);
            return newDate;
        });
    };

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days: JSX.Element[] = [];

        // Add days from previous month
        const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const daysInPrevMonth = getDaysInMonth(prevMonth);
        for (let i = 0; i < firstDay; i++) {
            const day = daysInPrevMonth - firstDay + i + 1;
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, day);
            const isDisabled = isDateDisabled(date);

            days.push(
                <button
                    key={`prev-${day}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOtherMonthDateSelect(day, -1);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, styles.otherMonth, {
                        [styles.disabled]: isDisabled,
                    })}
                >
                    {day}
                </button>,
            );
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isDisabled = isDateDisabled(date);
            const isSelected =
                selectedDate?.getDate() === day &&
                selectedDate?.getMonth() === currentDate.getMonth() &&
                selectedDate?.getFullYear() === currentDate.getFullYear();

            days.push(
                <button
                    key={day}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDateSelect(day);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, {
                        [styles.disabled]: isDisabled,
                        [styles.selected]: isSelected,
                    })}
                >
                    {day}
                </button>,
            );
        }

        // Calculate if we need 6 rows or 5 rows
        const totalDays = firstDay + daysInMonth;
        const numberOfRows = Math.ceil(totalDays / 7);
        const totalCells = numberOfRows * 7;

        // Add days from next month only up to the required cells
        const remainingDays = totalCells - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
            const isDisabled = isDateDisabled(date);

            days.push(
                <button
                    key={`next-${day}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOtherMonthDateSelect(day, 1);
                    }}
                    disabled={isDisabled}
                    className={classNames(styles.day, styles.otherMonth, {
                        [styles.disabled]: isDisabled,
                    })}
                >
                    {day}
                </button>,
            );
        }

        return days;
    };

    return (
        <div className={classNames(styles.datePicker, className)} ref={containerRef}>
            {!hideInput && (
                <div
                    ref={inputRef}
                    onClick={() => {
                        // Only toggle if we're using internal state (isOpenProp is false)
                        if (isOpenProp === false) {
                            setIsOpenInternal(!isOpenInternal);
                        }
                    }}
                >
                    <Input
                        type="text"
                        style={{ margin: 0, height: '44px' }}
                        value={value ? formatDateString(new Date(value), displayFormat) : ''}
                        readOnly={true}
                        disabled={disabled}
                        placeholder={placeholder}
                        inputStyle={{ cursor: 'pointer', width: '100%' }}
                        onChange={() => {}}
                        unit={unit}
                        unitStyle={{ padding: '0.2rem 0 0.2rem 4rem', ...unitStyle }}
                        isActive={isOpen}
                        showFloatingLabel={showFloatingLabel}
                    />
                </div>
            )}

            {isOpen && (!useFixedPosition || initialPositionCalculated) && (
                <div
                    ref={calendarRef}
                    className={styles.calendar}
                    onClick={(e) => e.stopPropagation()}
                    style={
                        useFixedPosition
                            ? {
                                  position: 'fixed',
                                  top: `${calendarPosition.top}px`,
                                  left: `${calendarPosition.left}px`,
                                  width: `${calendarWidth}px`,
                                  zIndex: 999999,
                              }
                            : {}
                    }
                >
                    <div className={styles.header}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeMonth(-1);
                            }}
                            className={styles.navButton}
                        >
                            <img src={collapseIcon} alt="Previous month" className={styles.prevMonth} />
                        </button>
                        <span>{formatDateString(currentDate, DATE_FORMATS.MONTH_FULL_YEAR)}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                changeMonth(1);
                            }}
                            className={styles.navButton}
                        >
                            <img src={collapseIcon} alt="Next month" className={styles.nextMonth} />
                        </button>
                    </div>

                    <div className={styles.weekdays}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <div key={day} className={styles.weekday}>
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className={styles.days}>{renderCalendar()}</div>
                </div>
            )}
        </div>
    );
};
